<template>
  <div>
    <b-row align-h="end"
      ><b-col cols="12" md="2" class="me-2 mb-2">
        <b-button
          class="align-right btn-fw w-100"
          @click="generateReport"
          v-b-tooltip.hover
          title="Export To PDF"
        >
          <b-icon icon="download" font-scale="1"></b-icon>
          {{ $translate("download_pdf") }}
        </b-button>
      </b-col>
    </b-row>

    <b-card ref="inventoryUsageReport">
      <b-card-header class="d-flex justify-content-between">
        <h4>{{ $translate("usage_display") }}</h4>
        <span>{{ startDt }} to {{ endDt }}</span>
      </b-card-header>
      <b-card-body>
        <b-table-simple bordered responsive>
          <b-thead>
            <b-tr>
              <b-th>{{ $translate("suppliers") }}</b-th>
              <b-th
                v-for="(vendor, vIdx) in vendors"
                :key="'vendor-name' + vIdx"
                :colspan="vendor.items.length"
                class="text-center"
              >
                {{ vendor.name }}
              </b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr>
              <b-th>{{
                (settings.bundled_product && settings.bundled_product.title) ||
                "Bundled Product"
              }}</b-th>
              <template v-for="(vendor, vIdx) in vendors">
                <b-th
                  v-for="(product, pIdx) in vendor.items"
                  :key="'product-name' + vIdx + pIdx"
                  class="text-center"
                >
                  {{
                    settings.bundled_product_type == "both"
                      ? `${product.name}/${product.container.name}`
                      : product.name
                  }}
                </b-th>
              </template>
            </b-tr>
            <b-tr v-for="(feature, hIdx) in features" :key="'feature' + hIdx">
              <b-th>{{ feature.title || $translate(feature.id) }}</b-th>
              <template v-for="(vendor, vIdx) in vendors">
                <b-td
                  v-for="(product, pIdx) in vendor.items"
                  :key="'product-name2' + vIdx + pIdx"
                  class="text-center"
                >
                  {{
                    (breakdowns[vIdx] &&
                      breakdowns[vIdx][pIdx] &&
                      breakdowns[vIdx][pIdx][feature.id]) ||
                    "0"
                  }}
                </b-td>
              </template>
            </b-tr>
          </b-tbody>
        </b-table-simple>
        <b-card class="mt-2 pageBreak">
          <b-card-header>
            <h6>
              {{
                (settings &&
                  settings.closing_unbundled_sub_product_count &&
                  settings.closing_unbundled_sub_product_count.title) ||
                $translate("closing_unbundled_sub_product_count")
              }}
            </h6></b-card-header
          >
          <b-card-body>
            <b-row class="justify-content-md-center gap-4">
              <b-col
                md="3"
                cols="12"
                v-for="(item, idx) in sizes"
                :key="'size--' + idx"
              >
                <b-card
                  :header="`${item.size} ${item.uom}`"
                  bg-variant="primary"
                  text-variant="white"
                  align="center"
                  header-tag="h6"
                  header-class="mt-2"
                  header-variant="primary"
                  header-border-variant="primary"
                >
                  <b-card-text>{{ item.qty }}</b-card-text>
                </b-card>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <b-card class="my-2">
          <b-card-header>
            <h6>
              {{
                (settings.secondary_items_closing_balances &&
                  settings.secondary_items_closing_balances.title) ||
                $translate("accessories")
              }}
            </h6></b-card-header
          >
          <b-card-body>
            <b-row class="justify-content-md-center gap-4">
              <b-col
                md="3"
                cols="12"
                v-for="(item, idx) in minorItemsClosingBalances"
                :key="'minorItems--' + idx"
              >
                <b-card
                  :header="item.name"
                  bg-variant="secondary"
                  text-variant="white"
                  align="center"
                  header-tag="h6"
                  header-class="mt-2"
                  header-variant="secondary"
                  header-border-variant="secondary"
                >
                  <b-card-text>{{ item.qty }}</b-card-text>
                </b-card>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import html2pdf from "html2pdf.js";
import dateTimeMixins from "../../mixins/dateTimeMixins";
export default {
  name: "DisplayDailyUsage",
  mixins: [dateTimeMixins],
  data() {
    return {
      breakdowns: [],
    };
  },

  props: {
    parseItem: {
      type: Function,
      require: true,
    },
    vendors: {
      type: Array,
      default: () => [],
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    headings: {
      type: Array,
      require: true,
    },

    minorItems: {
      type: Array,
      default: () => [],
    },
    orderTypes: {
      type: Array,
      default: () => [],
    },

    sectionId: { type: [Number], require: true },

    openingTime: { type: [Number], require: true },
    closingTime: { type: [Number], require: true },
  },
  computed: {
    features() {
      const { settings } = this;
      return this.headings
        .filter((id) => !!(settings[id] && settings[id].required))
        .map((id) => ({ id, title: settings[id].title }));
    },
    startDt() {
      return this.displayObjectDateTime(new Date(this.openingTime));
    },
    endDt() {
      return this.displayObjectDateTime(new Date(this.closingTime));
    },

    minorItemsClosingBalances() {
      const {
        minorItems,
        closingTime,
        openingTime,
        getBreakDowns,
        parseItem,
        sectionId,
      } = this;
      const _items = [];
      for (const product of minorItems) {
        const newItem = parseItem(product, sectionId);
        const { name } = newItem;
        const _breakdown = getBreakDowns(newItem, closingTime, openingTime);
        const qty = _breakdown.closingBalance || 0;
        _items.push({ name, qty });
      }
      _items.sort((a, b) => a.name - b.name);
      return _items;
    },

    sizes() {
      const { vendors, closingTime, openingTime, getBreakDowns } = this;
      const _sizes = [];
      for (const _vendor of vendors) {
        for (const product of _vendor.items) {
          const container = getBreakDowns(
            product.container,
            closingTime,
            openingTime
          );

          const { size } = product.container;
          const qty = container.closingBalance || 0;
          const uom = product.container.type.size_uom;

          let idx = _sizes.findIndex((x) => x.size == product.size);
          if (idx < 0) {
            _sizes.push({ size, qty, uom });
          } else {
            _sizes[idx].qty += qty;
          }
        }
      }
      _sizes.sort((a, b) => a.size - b.size);
      return _sizes;
    },
    transactionTypeOptions() {
      return this.$store.state.transactionType.options;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setBundledItemsBreakdowns();
    });
  },

  watch: {
    features: {
      handler: function (_features) {
        const { settings } = this;
        console.log("features:", { _features, settings });
      },
      deep: true,
    },
  },

  methods: {
    generateReport() {
      html2pdf(this.$refs.inventoryUsageReport, {
        margin: 1,
        filename: "inventoryUsageReport.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { dpi: 192, letterRendering: true },
        pagebreak: {
          before: ".pageBreak",
          avoid: ["h2", "h3", "h4", ".field"],
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "landscape",
        },
      });
    },
    setBundledItemsBreakdowns() {
      const {
        vendors,
        closingTime,
        openingTime,
        getBreakDowns,
        getBreakdownValue,
        headings,
      } = this;
      this.breakdowns.length = 0;
      console.log("vendors:", vendors);
      for (let vIdx = 0; vIdx < vendors.length; vIdx++) {
        const _vendor = vendors[vIdx];
        ////_breakdowns[vIdx] = {}
        this.breakdowns.push([]);
        for (let pIdx = 0; pIdx < _vendor.items.length; pIdx++) {
          const product = _vendor.items[pIdx];
          this.breakdowns[vIdx].push([]);
          //_breakdowns[vIdx][pIdx] = {}
          ////////////////////////

          const main = getBreakDowns(product, closingTime, openingTime);

          const container = getBreakDowns(
            product.container,
            closingTime,
            openingTime
          );

          for (const header of headings) {
            console.log("header:", header);
            const headerValue = getBreakdownValue(header, main, container);

            console.log("headerValue:", headerValue);

            this.$set(this.breakdowns[vIdx][pIdx], header, headerValue);
          }
        }
      }
      console.log("breakdowns", this.breakdowns);
      /* return _breakdowns */
    },

    getBreakDowns(product, closingTime, openingTime) {
      const convertTime = (dt_str) => this.getDateObject(dt_str).getTime();
      const section = product.section || {};
      let { transactions: _transactions } = section;
      //  const container = product.container

      _transactions = [...(_transactions || [])].map((x) => {
        x.unix_time = convertTime(x.updated_at);
        return x;
      });

      //sort in descending order
      _transactions.sort((a, b) => b.unix_time - a.unix_time);

      let closingIndex = _transactions.findIndex(
        (trx) => closingTime > trx.unix_time
      );

      if (closingIndex < 0) closingIndex = _transactions.length - 1;

      let openingIndex = _transactions.findIndex(
        (trx) => openingTime > trx.unix_time
      );

      if (openingIndex < 0) openingIndex = _transactions.length - 1;

      const closingBalance = _transactions
        .slice(0, closingIndex)
        .reduce((_closingBalance, trx) => {
          _closingBalance += -trx.added_amount;
          return _closingBalance;
        }, section.quantity_on_hand || 0);

      ////transactions window in correct order-- ascending
      const transactions = _transactions.slice(closingIndex, openingIndex);
      transactions.reverse();

      const openingBalance = transactions.reduce((_openingBalance, trx) => {
        _openingBalance += -trx.added_amount;
        return _openingBalance;
      }, closingBalance);

      console.log("breakdowns", {
        closingBalance,
        openingBalance,
        transactions,
      });

      return {
        closingBalance,
        openingBalance,
        transactions,
      };
    },

    getSales(transactions) {
      const {
        orderTypes,
        transactionTypeOptions: { entities, flows },
      } = this;

      const outboundFlow = flows["Outbound"];
      const inboundFlow = flows["Inbound"];
      const customerEnt = entities["Customer"];
      const officerEnt = entities["Officer"];
      const internalRef = entities["Internal"];

      const saleTypes = orderTypes.filter(
        (x) =>
          (x.transaction_type.flow === outboundFlow &&
            x.transaction_type.entity === customerEnt &&
            x.transaction_type.reference_entity === internalRef) ||
          (x.transaction_type.flow === inboundFlow &&
            x.transaction_type.entity === officerEnt &&
            x.transaction_type.reference_entity === customerEnt)
      );
      const saleTransactions = transactions.filter((x) =>
        saleTypes.some((y) => y.id == x.order.order_type_id)
      );
      console.log("saleTransactions", saleTransactions);
      return saleTransactions;
    },

    getReturns(transactions) {
      const {
        orderTypes,
        transactionTypeOptions: { entities, flows },
      } = this;

      const inboundFlow = flows["Inbound"];
      const customerEnt = entities["Customer"];
      const internalRef = entities["Internal"];

      const outboundFlow = flows["Outbound"];
      const officerRef = entities["Internal"];

      const returnTypes = orderTypes.filter(
        (x) =>
          (x.transaction_type.flow === inboundFlow &&
            x.transaction_type.entity === customerEnt &&
            x.transaction_type.reference_entity === internalRef) ||
          (x.transaction_type.flow === outboundFlow &&
            x.transaction_type.entity === officerRef &&
            x.transaction_type.reference_entity === customerEnt)
      );
      const returnTransactions = transactions.filter((x) =>
        returnTypes.some((y) => y.id == x.order.order_type_id)
      );
      console.log("returnTransactions", returnTransactions);
      return returnTransactions;
    },

    getPurchases(transactions) {
      const {
        orderTypes,
        transactionTypeOptions: { entities, flows },
      } = this;

      const outboundFlow = flows["Outbound"];
      const blackHoleRef = entities["Black Hole"];
      const internalEnt = entities["Internal"];

      const purchaseTypes = orderTypes.filter(
        (x) =>
          x.transaction_type.flow === outboundFlow &&
          x.transaction_type.entity === internalEnt &&
          x.transaction_type.reference_entity === blackHoleRef
      );
      const purchaseTransactions = transactions.filter((x) =>
        purchaseTypes.some((y) => y.id == x.order.order_type_id)
      );
      console.log("purchaseTransactions", purchaseTransactions);
      return purchaseTransactions;
    },

    getBreakdownValue(header, main, container) {
      console.log("getBreakdownValue:", { header, main, container });
      switch (header) {
        case "opening_bundled_count":
          return main.openingBalance || 0;
        case "opening_unbundled_sub_product_count":
          return (container.openingBalance || 0) - (main.openingBalance || 0);
        case "purchases_principal_product": {
          return this.getPurchases(main.transactions).reduce((_amt, trx) => {
            _amt += trx.added_amount;
            return _amt;
          }, 0);
        }
        case "purchases_sub_product": {
          {
            const totalbundled = this.getPurchases(main.transactions).reduce(
              (_amt, trx) => {
                _amt += trx.added_amount;
                return _amt;
              },
              0
            );

            const containers = this.getPurchases(container.transactions).reduce(
              (_amt, trx) => {
                _amt += trx.added_amount;
                return _amt;
              },
              0
            );

            return containers - totalbundled;
          }
        }
        case "sales_principal_product":
          return this.getSales(main.transactions).reduce((_amt, trx) => {
            _amt += -trx.added_amount;
            return _amt;
          }, 0);
        case "sales_sub_product": {
          const totalBundled = this.getSales(main.transactions).reduce(
            (_amt, trx) => {
              _amt += -trx.added_amount;
              return _amt;
            },
            0
          );
          const totalContainers = this.getSales(container.transactions).reduce(
            (_amt, trx) => {
              _amt += -trx.added_amount;
              return _amt;
            },
            0
          );
          return totalContainers - totalBundled;
        }
        case "returned_unbundled_sub_product_count":
          return this.getReturns(container.transactions).reduce((_amt, trx) => {
            _amt += trx.added_amount;
            return _amt;
          }, 0);
        case "closing_bundled_count":
          return main.closingBalance || 0;
        case "closing_unbundled_sub_product_count":
          return (container.closingBalance || 0) - (main.closingBalance || 0);
        case "total_unbundled_sub_product_count":
          return container.closingBalance || 0;
      }
    },
  },
};
</script>

<style></style>
